import React, { useState, useCallback, useEffect, useRef } from 'react';
import './PhoneReveal.css';

// Phone number stored as char-code fragments — never appears as a plain string in source.
// Assembled only in memory after human verification passes.
const _a = [43, 56, 56, 48];         // +880
const _b = [49, 55, 52, 56];         // 1748
const _c = [53, 57, 51, 57, 51, 53]; // 593935
const decode = () => [..._a, ..._b, ..._c].map((c) => String.fromCharCode(c)).join('');

// Generate a fresh arithmetic challenge
function newChallenge(): { q: string; answer: number } {
  const ops = ['+', '-', '×'] as const;
  const op = ops[Math.floor(Math.random() * ops.length)];
  let a: number, b: number, answer: number;
  if (op === '+') {
    a = 2 + Math.floor(Math.random() * 9);
    b = 2 + Math.floor(Math.random() * 9);
    answer = a + b;
  } else if (op === '-') {
    a = 7 + Math.floor(Math.random() * 9);
    b = 1 + Math.floor(Math.random() * 6);
    answer = a - b;
  } else {
    a = 2 + Math.floor(Math.random() * 7);
    b = 2 + Math.floor(Math.random() * 5);
    answer = a * b;
  }
  return { q: `${a} ${op} ${b}`, answer };
}

type State = 'masked' | 'challenge' | 'revealed' | 'error';

const PhoneReveal: React.FC = () => {
  const [uiState, setUiState] = useState<State>('masked');
  const [challenge, setChallenge] = useState(newChallenge);
  const [input, setInput] = useState('');
  const [shake, setShake] = useState(false);
  const [phone, setPhone] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const openChallenge = useCallback(() => {
    setChallenge(newChallenge());
    setInput('');
    setUiState('challenge');
  }, []);

  useEffect(() => {
    if (uiState === 'challenge') inputRef.current?.focus();
  }, [uiState]);

  const verify = useCallback(() => {
    const val = parseInt(input.trim(), 10);
    if (val === challenge.answer) {
      setPhone(decode());
      setUiState('revealed');
      setInput('');
    } else {
      setShake(true);
      setChallenge(newChallenge());
      setInput('');
      setTimeout(() => setShake(false), 600);
    }
  }, [input, challenge]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') verify();
      if (e.key === 'Escape') { setUiState('masked'); setInput(''); }
    },
    [verify]
  );

  const reset = useCallback(() => {
    setPhone('');
    setUiState('masked');
  }, []);

  return (
    <span className="phone-reveal-wrapper">
      <strong>Phone:</strong>{' '}

      {uiState === 'masked' && (
        <button
          className="phone-masked-btn"
          onClick={openChallenge}
          aria-label="Click to reveal phone number"
          // Honeypot: bots that look for tel: links or plain digits find nothing
        >
          <span className="phone-dots" aria-hidden="true">••••&nbsp;•••&nbsp;••••</span>
          <span className="phone-reveal-hint">click to reveal</span>
        </button>
      )}

      {uiState === 'challenge' && (
        <span className={`phone-captcha${shake ? ' shake' : ''}`}>
          <span className="captcha-label">What is&nbsp;<strong>{challenge.q}</strong>&nbsp;?</span>
          <input
            ref={inputRef}
            type="number"
            className="captcha-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="answer"
            autoComplete="off"
            aria-label="Enter CAPTCHA answer"
          />
          <button className="captcha-submit" onClick={verify}>✓</button>
          <button className="captcha-cancel" onClick={reset} aria-label="Cancel">✕</button>
        </span>
      )}

      {uiState === 'revealed' && (
        <span className="phone-revealed">
          <a href={`tel:${phone}`} className="phone-link">{phone}</a>
          <button className="phone-hide-btn" onClick={reset} aria-label="Hide phone number" title="Hide">⊗</button>
        </span>
      )}
    </span>
  );
};

export default PhoneReveal;
